import { useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getCommissionPayments,
  getCommissionSummary,
  toggleCommissionPayment,
  clearToggleError,
} from "../store/slices/adminReportSlice";
import useFilterDropdowns from "./useFilterDropdowns";
import useUrlFilters from "./useUrlFilters";
import { showApiError, showApiSuccess } from "../utils/toast";

// ─── constants ────────────────────────────────────────────────────────────────

export const STATUS_OPTIONS = [
  { value: "all",    label: "All" },
  { value: "paid",   label: "Paid" },
  { value: "unpaid", label: "Unpaid" },
];

export const MONTHS = [
  { value: 1,  label: "January" },
  { value: 2,  label: "February" },
  { value: 3,  label: "March" },
  { value: 4,  label: "April" },
  { value: 5,  label: "May" },
  { value: 6,  label: "June" },
  { value: 7,  label: "July" },
  { value: 8,  label: "August" },
  { value: 9,  label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const now = new Date();
const currentYear = now.getFullYear();
const currentMonth = now.getMonth() + 1;
// Default to previous month (backend pattern — settlements are for past months)
const defaultMonth = currentMonth === 1 ? 12 : currentMonth - 1;
const defaultYear = currentMonth === 1 ? currentYear - 1 : currentYear;

export const YEARS = Array.from(
  { length: currentYear - 2023 },
  (_, i) => currentYear - i
);

// ─── hook ─────────────────────────────────────────────────────────────────────

export const useCommissionPayments = () => {
  const dispatch = useDispatch();

  // ── Redux state ────────────────────────────────────────────────────────────
  const {
    payments, paymentsPagination, paymentsFilter,
    paymentsLoading, paymentsError,
    summary, summaryLoading, summaryError,
    toggleLoadingMap,
  } = useSelector((state) => state.adminReport);

  // ── filters (synced with URL) ──────────────────────────────────────────────
  const { filters, updateFilter, resetFilters, currentPage, setPage } = useUrlFilters(
    {
      month: defaultMonth,
      year: defaultYear,
      status: "all",
      search: "",
      district_id: "",
      taluka_id: "",
      category_id: "",
      sub_category_id: "",
    },
    { numberFields: ["month", "year"] }
  );

  // ── dropdown data (reusable hook) ───────────────────────────────────────────
  const dropdowns = useFilterDropdowns({
    district_id: filters.district_id,
    category_id: filters.category_id,
  });

  // Debounce search
  const searchTimerRef = useRef(null);

  // ── fetch commission data ──────────────────────────────────────────────────
  const fetchData = useCallback(async (currentFilters = filters, page = currentPage) => {
    await Promise.all([
      dispatch(getCommissionPayments({ ...currentFilters, page, per_page: 15 })),
      dispatch(getCommissionSummary({
        month: currentFilters.month,
        year: currentFilters.year,
        district_id: currentFilters.district_id,
        taluka_id: currentFilters.taluka_id,
        category_id: currentFilters.category_id,
        sub_category_id: currentFilters.sub_category_id,
      })),
    ]);
  }, [dispatch, filters, currentPage]);

  // ── auto-fetch when filters change (except search — debounced separately) ──
  useEffect(() => {
    fetchData(filters, currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.month,
    filters.year,
    filters.status,
    filters.district_id,
    filters.taluka_id,
    filters.category_id,
    filters.sub_category_id,
    currentPage,
  ]);

  // ── debounced search ───────────────────────────────────────────────────────
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      fetchData(filters, 1);
    }, 400);
    return () => clearTimeout(searchTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search]);

  // ── actions ────────────────────────────────────────────────────────────────
  const handlePageChange = useCallback((page) => {
    setPage(page);
  }, [setPage]);

  const handleTogglePayment = useCallback(async (uuid, currentIsPaid) => {
    const newIsPaid = !currentIsPaid;
    try {
      const result = await dispatch(toggleCommissionPayment({ uuid, is_paid: newIsPaid }));
      if (toggleCommissionPayment.fulfilled.match(result)) {
        showApiSuccess(newIsPaid ? "Marked as paid" : "Marked as unpaid");
      } else {
        showApiError(null, result.payload || "Failed to update status");
        dispatch(clearToggleError());
      }
    } catch {
      showApiError(null, "Failed to update status");
    }
  }, [dispatch]);

  const refreshData = useCallback(() => fetchData(filters, currentPage), [fetchData, filters, currentPage]);

  // ── derived helpers ────────────────────────────────────────────────────────
  const selectedMonthLabel = MONTHS.find((m) => m.value === filters.month)?.label || "";

  return {
    // Data
    payments,
    paymentsPagination,
    paymentsFilter,
    paymentsLoading,
    paymentsError,
    summary,
    summaryLoading,

    // Filters
    filters,
    updateFilter,
    handlePageChange,
    resetFilters,

    // Dropdown options
    dropdowns,

    // Toggle
    toggleLoadingMap,
    handleTogglePayment,

    // Actions
    refreshData,

    // Constants
    STATUS_OPTIONS,
    MONTHS,
    YEARS,
    selectedMonthLabel,
  };
};

export default useCommissionPayments;
